/**
 * HomeCards — image-forward card grid for the Cluster-1 Home tabs.
 *
 * Payload-driven (029 US1). Every card on the strip — including the jasenedut
 * ("Edut ja alennukset") tile — is a published `home_card` row read through
 * `useCuratedCards()` (the `ContentReaderPort`). There is no frontend-hardcoded
 * card and no per-slug tab/image map: each card's tab membership, visual span,
 * image, navigation target, and order come from its authored payload
 * (`card.tab` / `card.size` / `card.imageUrl` / `card.linkSlug` / the adapter's
 * `curatedOrder` sort). This lifts the pre-029 `CARD_RESOURCES_BY_SLUG`
 * source-of-truth and the 6th hardcoded card.
 *
 * Image resolution (029 T011 / research D3) lives in `homeCardImage.ts`
 * (`resolveHomeCardImage`): authored `card.imageUrl` first, then the slug-keyed
 * bundled image as an ABSENCE fallback for the five known live slugs, then a
 * generic default. A never-seen slug renders (no silent drop, FR-002 / SC-006);
 * a dev-mode `console.warn` flags only the generic-default case as a non-fatal
 * observability note.
 *
 * State matrix (FR-014): loading -> skeleton strip; error -> inline Finnish
 * message (NOT a `PortErrorBoundary` throw — Home must stay reachable per
 * FR-054, the curated strip degrades gracefully); empty -> a defined empty
 * state; populated -> the authored cards for the active tab. Because jasenedut
 * is now a payload card, it degrades WITH the strip on a read error (it is no
 * longer a frontend-resident affordance that survives a hook failure) — Home
 * stays reachable via the inline error/empty branches.
 *
 * Draft-injection seam (029 T016, consumed by US4): when a `cards` prop is
 * supplied, the strip renders that injected draft directly and the production
 * read is disabled (no second read path — P4 / P7 / FR-013), mirroring
 * `ArticleDetailPage`'s `article?` prop.
 *
 * Behaviour invariants:
 *   - Cards render as `<button type="button">` controls; a `page` target
 *     resolves to either its registered short content path or the generic
 *     `/content/:slug` detail route. Route and contact targets retain their
 *     authored behavior.
 *   - `<img>` alt is empty: the title is the button's accessible name.
 *   - `card.title` is locale-aware seed data; locale is read from
 *     `useLanguage().active` (the adapter falls back to fi for non-fi sessions).
 *   - No per-card "Esimerkki" chip / category label (customer feedback, D-049).
 *     The route-level demo posture continues to satisfy the Demo/Fake
 *     Transparency Contract.
 *
 * Spec references: 029 US1 (T010-T016) in
 * `specs/029-cms-driven-member-surfaces/tasks.md`; FR-002 / FR-014 / FR-054 /
 * SC-006 in `specs/029-cms-driven-member-surfaces/spec.md`; TA23 in
 * `specs/003-content-and-knowledge/tasks.md`.
 */

import { type JSX, useState } from 'react';
import { type NavigateFunction, useNavigate } from 'react-router';

import { isVideoUrl } from '../../components/cms/isVideoUrl.js';
import type { CuratedCard } from '../../content/ContentReaderPort.js';
import { useCuratedCards } from '../../content/hooks/useCuratedCards.js';
import type { MessageKeys } from '../../i18n/messages/index.js';
import { useLanguage } from '../../i18n/useLanguage.js';
import { resolveContentPagePath } from '../../shell/contentPagePath.js';
import { bundledFallbackFor, resolveHomeCardImage } from './homeCardImage.js';

/**
 * Tab discriminator. Mirrors the `HomeTab` type in `Home.tsx` and the read
 * shape's `CuratedCard.tab` member vocabulary (`'lakikaveri' | 'palvelutEdut'`);
 * the per-tab filter is a direct equality check against `card.tab`.
 */
export type HomeCardsTab = 'lakikaveri' | 'palvelutEdut';

const cardsGridClasses = 'grid grid-cols-2 gap-3';
// Image-forward card. The `<button>` is the positioning context so the
// gradient overlay (`absolute inset-x-0 bottom-0`) and the text overlay
// can anchor against the card edge. The image sits as a full-bleed
// background (`absolute inset-0 h-full w-full object-cover`) so the
// photograph fills the entire card area while the title reads against
// the gradient at the bottom.
//
// Hover treatment mirrors the prototype: `group` on the card +
// `group-hover:scale-105 transition-transform duration-300` on the img
// produces the gentle "lift towards me" zoom that signals tappability
// without destabilising layout.
const cardClasses =
  'group relative block w-full overflow-hidden rounded-lg bg-card text-card-foreground text-left h-45 cursor-pointer transition active:scale-95 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';
const cardFullWidthClass = 'col-span-2';
const cardImageClasses =
  'absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105';
// Bottom gradient overlay. `from-foreground/85` produces a near-opaque
// dark band at the bottom so the title stays legible (≥4.5:1) on top of
// the photographic background; `to-transparent` fades upward so the
// image's upper area reads cleanly.
const cardOverlayClasses =
  'pointer-events-none absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-foreground/85 to-transparent';
const cardTextWrapperClasses = 'absolute inset-x-0 bottom-0 flex flex-col gap-1 p-3';
const cardTitleClasses = 'min-w-0 wrap-anywhere text-base font-semibold text-background';
// Skeleton + error/empty tokens. `bg-muted` matches the Home deferred-notice
// tile; `animate-pulse` is Tailwind's stock placeholder shimmer.
const skeletonCardClasses = 'h-45 w-full animate-pulse rounded-lg bg-muted';
const noticeStateClasses = 'rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground';

interface CardViewProps {
  readonly title: string;
  readonly imageSrc: string;
  /** Author-supplied alt text for the card image (024-cms page-editor parity / D4). */
  readonly imageAlt: string | null;
  /**
   * The bundled photo to show if `imageSrc` FAILS to load at runtime (Fix D,
   * defense-in-depth). The slug-keyed bundled photo for a known slug, else the
   * generic default — so a load failure degrades to a real photo, never a bare
   * gradient. Computed by the caller via `bundledFallbackFor(card.slug)`.
   */
  readonly fallbackSrc: string;
  readonly fullWidth: boolean;
  readonly onClick: () => void;
}

function CardView({
  title,
  imageSrc,
  imageAlt,
  fallbackSrc,
  fullWidth,
  onClick,
}: CardViewProps): JSX.Element {
  // Card structure (image-forward, interactive control):
  //   1. background photograph — fills the card area with `object-cover`
  //      so the photo covers the area regardless of aspect ratio. The
  //      `<img>` carries the AUTHORED alt (024-cms D4) as its own text
  //      alternative, falling back to decorative `alt=""` when blank —
  //      the button's `aria-label={title}` keeps the CONTROL's accessible
  //      name exactly the title regardless, so an authored alt never
  //      concatenates into the button name. An `onError` swap recovers a
  //      load failure (Fix D): if the resolved `imageSrc` fails (e.g. a
  //      /media 404), the card shows the bundled `fallbackSrc` rather than
  //      a bare gradient.
  //   2. gradient overlay — anchored at the bottom edge, fades to
  //      transparent upward so the title stays legible against the
  //      image without occluding the artwork above.
  //   3. text wrapper — title (base, semibold) rendered in
  //      `text-background` against the gradient's dark foreground tint,
  //      visually mirroring the button's `aria-label`.
  //
  // `src` is DERIVED from the live `imageSrc` prop — never copied into
  // state. State holds only a one-way `errored` flag. Copying the prop
  // into state would freeze `src` when the same-slug card (stable
  // `key={card.slug}`) reconciles IN PLACE with a newly-resolved image
  // (background refetch, draft re-preview, locale cache hit) — the image
  // would then lag the `title` that updates beside it. `errored` is
  // re-armed during render when `imageSrc` changes, so a fixed authored
  // URL retries the load (React's adjust-state-during-render idiom — no
  // `useEffect`, no extra commit).
  const [errored, setErrored] = useState(false);
  const [prevImageSrc, setPrevImageSrc] = useState(imageSrc);
  if (imageSrc !== prevImageSrc) {
    setPrevImageSrc(imageSrc);
    setErrored(false);
  }
  const src = errored ? fallbackSrc : imageSrc;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={title}
      className={`${cardClasses}${fullWidth ? ` ${cardFullWidthClass}` : ''}`}
    >
      {/* 024-cms Phase U: a card's authored media may be a video `/media/...mp4`
          URL. Render a `<video>` poster frame (no `controls` — the card itself
          is the navigation control; the background must not capture the click)
          for a video URL, otherwise the full-bleed `<img>` with the Fix D
          load-failure fallback. */}
      {isVideoUrl(imageSrc) ? (
        // Decorative poster frame (`muted`, no `controls`): `aria-hidden` keeps
        // it out of the AT tree and `tabIndex={-1}` keeps it out of the tab
        // order, so the card `<button>` stays the sole focusable/interactive
        // element (the title carries the accessible name).
        <video src={imageSrc} className={cardImageClasses} muted tabIndex={-1} aria-hidden="true" />
      ) : (
        <img
          src={src}
          alt={imageAlt?.trim() ?? ''}
          className={cardImageClasses}
          onError={() => {
            // Flip to the bundled fallback once (Fix D). If the fallback
            // itself 404s, this same-value `setErrored(true)` makes React
            // bail — no re-render, no loop.
            if (!errored) {
              setErrored(true);
            }
          }}
        />
      )}
      <span className={cardOverlayClasses} aria-hidden="true" />
      <span className={cardTextWrapperClasses}>
        <span className={cardTitleClasses}>{title}</span>
      </span>
    </button>
  );
}

/**
 * Skeleton strip for the loading state. Three pulse blocks approximate
 * the rhythm of the populated grid (2-column row + 1 full-width); the
 * grid container itself is reused so the skeleton lays out identically
 * to the populated state. Carries `role="status"` + `aria-busy` so AT
 * announces the in-flight state, plus `aria-label` (the wrapper has no
 * visible text, so the label is what screen readers announce when the
 * status region appears — mirrors `ListSkeleton` in `ArticleListPage`).
 */
function HomeCardsSkeleton({ t }: { readonly t: (key: MessageKeys) => string }): JSX.Element {
  return (
    <div
      className={cardsGridClasses}
      role="status"
      aria-busy="true"
      aria-label={t('home.cards.loading')}
      data-testid="home-cards-skeleton"
    >
      <div aria-hidden="true" className={skeletonCardClasses} />
      <div aria-hidden="true" className={skeletonCardClasses} />
      <div aria-hidden="true" className={`${skeletonCardClasses} ${cardFullWidthClass}`} />
    </div>
  );
}

/**
 * Renders the image-forward card grid for the given Home tab.
 *
 * Production path: reads the curated rows from `useCuratedCards()` and renders
 * the cards whose `tab` matches the active tab. Preview path (029 T016): when
 * a `cards` prop is supplied, renders that injected draft strip directly and
 * disables the production read (no second read path). The translator is passed
 * in as a prop so the parent owns the `useTranslate` subscription — keeps
 * `HomeCards` rendering pure with respect to the i18n hook.
 */
export function HomeCards({
  tab,
  t,
  cards,
  onContact,
}: {
  readonly tab: HomeCardsTab;
  readonly t: (key: MessageKeys) => string;
  // `?: T | undefined` under `exactOptionalPropertyTypes`: the parent forwards a
  // possibly-`undefined` `cards` (the production callsites omit it). Present-
  // undefined is semantically identical to absent (the production read path).
  readonly cards?: readonly CuratedCard[] | undefined;
  /** The shell injects the one existing compose-wizard action. */
  readonly onContact?: (() => void) | undefined;
}): JSX.Element {
  const navigate = useNavigate();
  const { active: locale } = useLanguage();
  // The hook is called UNCONDITIONALLY (Rules of Hooks) but disabled on the
  // injected-draft path so NO production read fires — the preview renders the
  // injected draft with no second read path (P4 / P7 / FR-013), mirroring
  // `ArticleDetailPage`'s `useArticle(slug, { enabled: props.article === undefined })`.
  const curated = useCuratedCards({ locale, enabled: cards === undefined });

  // Injected-draft path (US4 preview): the draft is in hand, so the hook's
  // loading / error states do not apply — render the provided strip directly.
  if (cards !== undefined) {
    return renderStrip(cards, tab, navigate, onContact, t);
  }

  if (curated.isPending) {
    return <HomeCardsSkeleton t={t} />;
  }

  if (curated.isError) {
    // Error state: a small inline message (NOT a boundary throw). Home must
    // remain reachable (FR-054); the curated strip degrades gracefully. Since
    // jasenedut is now a payload card, it degrades with the strip here.
    return (
      <div className={cardsGridClasses}>
        <p className={`${noticeStateClasses} ${cardFullWidthClass}`} role="alert">
          {t('home.cards.errorState')}
        </p>
      </div>
    );
  }

  return renderStrip(curated.data, tab, navigate, onContact, t);
}

/**
 * Render the populated/empty grid for one tab from a resolved card list
 * (shared by the production and injected-draft paths). Filters to the active
 * tab preserving the adapter's `curatedOrder`, renders the defined empty state
 * (FR-014) when the tab has no cards, and resolves each card's image
 * authored-first. A card whose image falls through to the generic default
 * emits a non-fatal dev-mode note (the card still renders — no silent drop,
 * FR-002 / SC-006).
 */
function renderStrip(
  source: readonly CuratedCard[],
  tab: HomeCardsTab,
  navigate: NavigateFunction,
  onContact: (() => void) | undefined,
  t: (key: MessageKeys) => string,
): JSX.Element {
  const tabCards = source.filter((card) => card.tab === tab);

  if (tabCards.length === 0) {
    return (
      <div className={cardsGridClasses}>
        <p className={`${noticeStateClasses} ${cardFullWidthClass}`}>{t('home.cards.empty')}</p>
      </div>
    );
  }

  return (
    <div className={cardsGridClasses}>
      {tabCards.map((card) => {
        const image = resolveHomeCardImage(card);
        if (image.usedGenericDefault && import.meta.env.DEV) {
          // biome-ignore lint/suspicious/noConsole: dev-only observability for a curated card that resolved no authored or known-slug image
          console.warn(
            `HomeCards: curated card "${card.slug}" resolved no authored imageUrl and no known-slug bundled image; rendering the generic default. Author an imageUrl on the home_card or add the slug to homeCardImage.ts`,
          );
        }
        return (
          <CardView
            key={card.slug}
            title={card.title}
            imageSrc={image.src}
            imageAlt={card.imageAlt}
            fallbackSrc={bundledFallbackFor(card.slug)}
            fullWidth={card.size === 'large'}
            onClick={() => {
              const target = card.target ?? { kind: 'page' as const, linkSlug: card.linkSlug };
              switch (target.kind) {
                case 'contact':
                  onContact?.();
                  return;
                case 'route':
                  navigate(target.route);
                  return;
                case 'page':
                  navigate(resolveContentPagePath(target.linkSlug));
                  return;
              }
            }}
          />
        );
      })}
    </div>
  );
}
